import Box from "@mui/material/Box"
import { useTheme } from "@mui/material/styles"

/** How far into a panel a fade reaches. */
export const FADE_SIZE_PX = 28

export type EdgeT = "top" | "bottom" | "left" | "right"

interface Props {
    edge: EdgeT
    /**
     * Where the fade starts and stops, in px from the panel's edges: `top` for a fade along
     * the top edge is how far below the panel's top it begins (under a sticky header), the
     * others where it ends (short of a scrollbar, a sticky column).
     */
    inset?: Partial<Record<EdgeT, number>>
    zIndex?: number
}

/**
 * One edge of a scrollable panel fading into the background, saying "there is more past
 * here" (see FadingScrollContainer). Drawn over the content, so it never takes a click.
 */
const EdgeFade = ({ edge, inset = {}, zIndex = 1 }: Props) => {
    const theme = useTheme()
    const isVertical = edge === "top" || edge === "bottom"
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                top: inset.top ?? 0,
                bottom: inset.bottom ?? 0,
                left: inset.left ?? 0,
                right: inset.right ?? 0,
                // pinned to its own edge, the size of the fade across it
                ...(isVertical
                    ? { [edge === "top" ? "bottom" : "top"]: "auto", height: FADE_SIZE_PX }
                    : { [edge === "left" ? "right" : "left"]: "auto", width: FADE_SIZE_PX }),
                pointerEvents: "none",
                zIndex,
                background: `linear-gradient(to ${edge}, transparent, ${theme.palette.background.default} 85%)`,
            }}
        />
    )
}

export default EdgeFade
